import {useState} from 'react';
import {IAdditionalInfo} from '../../../../models/additionalInfo';
import {ISiteType} from '../../../../models/siteType';
import {ISubstrateOption} from '../../../../models/substrateOption';
import {convertToDms, parseCoordinateComponent} from '../../../../utils/cordinates_helpers';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

interface Props {
    form: ICreateCleanUpForm;
    substrateOptions: ISubstrateOption[];
    siteTypes: ISiteType[];
    additionalInfo: IAdditionalInfo[];
    onChange: (updates: Partial<ICreateCleanUpForm>) => void;
    showErrors?: boolean;
}

export default function Step3Location({
    form,
    substrateOptions,
    siteTypes,
    additionalInfo,
    onChange,
    showErrors,
}: Props) {
    const [latText, setLatText] = useState(() =>
        form.latitude ? convertToDms(parseFloat(form.latitude), false) : ''
    );
    const [lngText, setLngText] = useState(() =>
        form.longitude ? convertToDms(parseFloat(form.longitude), true) : ''
    );
    const [latError, setLatError] = useState(false);
    const [lngError, setLngError] = useState(false);

    const commitLat = () => {
        if (!latText.trim()) {
            setLatError(false);
            onChange({latitude: ''});
            return;
        }
        const parsed = parseCoordinateComponent(latText, 'lat');
        if (parsed === null) {
            setLatError(true);
            return;
        }
        setLatError(false);
        setLatText(convertToDms(parsed, false));
        onChange({latitude: String(parsed)});
    };

    const commitLng = () => {
        if (!lngText.trim()) {
            setLngError(false);
            onChange({longitude: ''});
            return;
        }
        const parsed = parseCoordinateComponent(lngText, 'lng');
        if (parsed === null) {
            setLngError(true);
            return;
        }
        setLngError(false);
        setLngText(convertToDms(parsed, true));
        onChange({longitude: String(parsed)});
    };

    const toggleSiteType = (id: string) => {
        const updated = form.site_types.includes(id)
            ? form.site_types.filter((s) => s !== id)
            : [...form.site_types, id];
        onChange({site_types: updated});
    };

    const toggleAdditionalInfo = (id: string) => {
        const updated = form.additional_location_info.includes(id)
            ? form.additional_location_info.filter((s) => s !== id)
            : [...form.additional_location_info, id];
        onChange({additional_location_info: updated});
    };

    return (
        <div className={s.stepBody}>
            {/* Site Types */}
            {siteTypes.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Site Types</div>
                    <div className={s.checkboxGroup}>
                        {siteTypes.map((st) => (
                            <label key={st.id} className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.site_types.includes(st.id)}
                                    onChange={() => toggleSiteType(st.id)}
                                />
                                {st.title}
                            </label>
                        ))}
                    </div>
                </>
            )}

            <div className={s.subSectionTitle}>Coordinates</div>

            <div className={s.twoCol}>
                <div className={s.formGroup}>
                    <label className={s.required}>Latitude</label>
                    <input
                        type="text"
                        value={latText}
                        onChange={(e) => {
                            setLatText(e.target.value);
                            setLatError(false);
                        }}
                        onBlur={commitLat}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData('text');
                            const parsed = parseCoordinateComponent(pasted, 'lat');
                            if (parsed === null) return;
                            e.preventDefault();
                            setLatError(false);
                            setLatText(convertToDms(parsed, false));
                            onChange({latitude: String(parsed)});
                        }}
                        className={`${s.input}${(latError || (showErrors && !form.latitude)) ? ` ${s.inputError}` : ''}`}
                        placeholder={`e.g., 37.9838 or 37°59'1.68"N`}
                    />
                    {latError && (
                        <span className={s.fieldError}>
                            Enter a valid latitude in decimal (e.g., 37.9838) or DMS (e.g., 37°59&apos;1.68&quot;N) format
                        </span>
                    )}
                    {!latError && showErrors && !form.latitude && (
                        <span className={s.fieldError}>Latitude is required</span>
                    )}
                    {!latError && form.latitude && (
                        <span className={s.coordNote}>
                            {form.latitude} · {convertToDms(parseFloat(form.latitude), false)}
                        </span>
                    )}
                </div>
                <div className={s.formGroup}>
                    <label className={s.required}>Longitude</label>
                    <input
                        type="text"
                        value={lngText}
                        onChange={(e) => {
                            setLngText(e.target.value);
                            setLngError(false);
                        }}
                        onBlur={commitLng}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData('text');
                            const parsed = parseCoordinateComponent(pasted, 'lng');
                            if (parsed === null) return;
                            e.preventDefault();
                            setLngError(false);
                            setLngText(convertToDms(parsed, true));
                            onChange({longitude: String(parsed)});
                        }}
                        className={`${s.input}${(lngError || (showErrors && !form.longitude)) ? ` ${s.inputError}` : ''}`}
                        placeholder={`e.g., 23.7275 or 23°43'39"E`}
                    />
                    {lngError && (
                        <span className={s.fieldError}>
                            Enter a valid longitude in decimal (e.g., 23.7275) or DMS (e.g., 23°43&apos;39&quot;E) format
                        </span>
                    )}
                    {!lngError && showErrors && !form.longitude && (
                        <span className={s.fieldError}>Longitude is required</span>
                    )}
                    {!lngError && form.longitude && (
                        <span className={s.coordNote}>
                            {form.longitude} · {convertToDms(parseFloat(form.longitude), true)}
                        </span>
                    )}
                </div>
            </div>

            <div className={s.subSectionTitle}>Region</div>

            <div className={s.formGroup}>
                <label>Location Name</label>
                <input
                    type="text"
                    value={form.location_name}
                    onChange={(e) => onChange({location_name: e.target.value})}
                    className={s.input}
                    placeholder="e.g., North Beach Area"
                />
            </div>

            <div className={s.formGroup}>
                <label>City / Island</label>
                <input
                    type="text"
                    value={form.city_island}
                    onChange={(e) => onChange({city_island: e.target.value})}
                    className={s.input}
                />
            </div>

            <div className={s.subSectionTitle}>Morphology</div>

            <div className={s.twoCol}>
                <div className={s.formGroup}>
                    <label>Area (sqm)</label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.area}
                        onChange={(e) => onChange({area: e.target.value})}
                        className={s.input}
                        placeholder="e.g., 500"
                    />
                </div>
                <div className={s.formGroup}>
                    <label>Length (m)</label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.length}
                        onChange={(e) => onChange({length: e.target.value})}
                        className={s.input}
                        placeholder="e.g., 200"
                    />
                </div>
            </div>

            <div className={s.formGroup}>
                <label>Substrate</label>
                <select
                    value={form.substrate_id}
                    onChange={(e) => onChange({substrate_id: e.target.value})}
                    className={s.select}
                >
                    <option value="">Select substrate</option>
                    {substrateOptions.map((so) => (
                        <option key={so.id} value={so.id}>
                            {so.title}
                        </option>
                    ))}
                </select>
            </div>

            {/* Additional Location Info */}
            {additionalInfo.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Additional Location Info</div>
                    <div className={s.checkboxGroup}>
                        {additionalInfo.map((ai) => (
                            <label key={ai.id} className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.additional_location_info.includes(ai.id)}
                                    onChange={() => toggleAdditionalInfo(ai.id)}
                                />
                                {ai.title}
                            </label>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
